import { useCallback } from 'react'

import { SaveProductSettingsAiStructureDocument } from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'

export type SaveAiStructureResult =
  | { success: true; valueJson: string }
  | { success: false; errors: string[] }

interface Params {
  productId: string
  settingName: string
  companyId: string
}

// Framework-supplied save hook for `ai_structure` settings.
//
// Reads are owned by the consumer (the product renders these
// settings through its own component and fetches via its own GraphQL
// resource), so this hook is intentionally minimal: it only exposes
// the framework's `save` verb, which validates the JSON against the
// manifest schema and forwards persistence to the consumer
// interactor.
//
// `save(value)` accepts any JSON-serialisable value; the hook stringifies
// it before calling the mutation. The caller is responsible for
// deciding the shape of the value (it must match the JSON Schema
// published on the matching ProductSettingsDefinition).
export const useProductSettingsAiStructureSave = ({
  productId,
  settingName,
  companyId,
}: Params) => {
  const [save, { loading }] = useMutation(SaveProductSettingsAiStructureDocument, {
    extractorFn: (data) => data.productSettingsMutations.saveProductsettingsAiStructure,
  })

  const saveAiStructure = useCallback(
    async (value: unknown): Promise<SaveAiStructureResult> => {
      // `JSON.stringify` can throw on circular refs or non-serialisable
      // primitives (e.g. `BigInt`). The hook accepts `unknown`, so we
      // cannot rely on the caller having pre-validated the shape —
      // catch and surface as a normal validation-style failure rather
      // than letting it bubble up as an unhandled exception.
      let valueJson: string
      try {
        valueJson = JSON.stringify(value)
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e)
        return { success: false, errors: [`Failed to serialise value to JSON: ${message}`] }
      }

      const result = await save({
        variables: {
          productId,
          companyId,
          settingName,
          valueJson,
        },
      })

      const handled = handleResult(result, () => {})

      if (handled.success) {
        return { success: true, valueJson: handled.data.aiStructure?.valueJson ?? '' }
      }

      return {
        success: false,
        errors: handled.errors.map((e) => {
          if (e.__typename === 'StructuredError') {
            return `${e.field}: ${e.messages.join(', ')}`
          }
          if (e.__typename === 'SimpleError') {
            return e.message
          }
          return 'Unknown error'
        }),
      }
    },
    [save, productId, companyId, settingName]
  )

  return { saveAiStructure, loading }
}
